import { lazy, Suspense, type JSX } from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { AppShell } from '../layout/AppShell';
import { LoadingState } from '../components/LoadingState';

/**
 * performance-design-web-oberflaeche, Abschnitt "Bundle-Größe und
 * Code-Splitting": routen-basiertes Code-Splitting via `React.lazy`. Alle
 * drei Markierungsmodus-Routen laden denselben Chunk (`marking-mode/MarkingMode`),
 * sodass er nur einmal pro Sitzung heruntergeladen wird.
 */
const Dashboard = lazy(() => import('../screens/Dashboard').then((m) => ({ default: m.Dashboard })));
const SourceTree = lazy(() => import('../screens/SourceTree').then((m) => ({ default: m.SourceTree })));
const SourceDetail = lazy(() => import('../screens/SourceDetail').then((m) => ({ default: m.SourceDetail })));
const Models = lazy(() => import('../screens/Models').then((m) => ({ default: m.Models })));
const Review = lazy(() => import('../screens/Review').then((m) => ({ default: m.Review })));
const ChangeDetail = lazy(() => import('../screens/ChangeDetail').then((m) => ({ default: m.ChangeDetail })));
const Matrix = lazy(() => import('../screens/Matrix').then((m) => ({ default: m.Matrix })));
const Templates = lazy(() => import('../screens/Templates').then((m) => ({ default: m.Templates })));
const Settings = lazy(() => import('../screens/Settings').then((m) => ({ default: m.Settings })));
const NewSourceMarkingMode = lazy(() =>
  import('../screens/marking-mode/MarkingMode').then((m) => ({ default: m.NewSourceMarkingMode })),
);
const AddMarkingsMarkingMode = lazy(() =>
  import('../screens/marking-mode/MarkingMode').then((m) => ({ default: m.AddMarkingsMarkingMode })),
);
const RemapMarkingMode = lazy(() =>
  import('../screens/marking-mode/MarkingMode').then((m) => ({ default: m.RemapMarkingMode })),
);

function withSuspense(element: JSX.Element): JSX.Element {
  return <Suspense fallback={<LoadingState loading label="Wird geladen …">{<></>}</LoadingState>}>{element}</Suspense>;
}

const router = createBrowserRouter([
  {
    path: '/',
    element: <AppShell />,
    children: [
      { index: true, element: withSuspense(<Dashboard />) },
      { path: 'sources', element: withSuspense(<SourceTree />) },
      { path: 'sources/new', element: withSuspense(<NewSourceMarkingMode />) },
      { path: 'sources/:sourceId', element: withSuspense(<SourceDetail />) },
      { path: 'sources/:sourceId/markings/new', element: withSuspense(<AddMarkingsMarkingMode />) },
      { path: 'markings/:markingId/remap', element: withSuspense(<RemapMarkingMode />) },
      { path: 'models', element: withSuspense(<Models />) },
      { path: 'models/:modelId/review', element: withSuspense(<Review />) },
      { path: 'reviews/new', element: withSuspense(<Review />) },
      { path: 'changes/:changeId', element: withSuspense(<ChangeDetail />) },
      { path: 'matrix', element: withSuspense(<Matrix />) },
      { path: 'templates', element: withSuspense(<Templates />) },
      { path: 'settings', element: withSuspense(<Settings />) },
    ],
  },
]);

export function AppRouter(): JSX.Element {
  return <RouterProvider router={router} />;
}
